import React, { useEffect, useState } from 'react';
import { Layout, Menu, Button, Avatar, Dropdown, MenuProps, Breadcrumb } from 'antd';
import {
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  ProjectOutlined,
  CrownOutlined,
  AppstoreOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { selectUser, clearUser, setUser } from "../../store/userSlice";
import { auth } from "../../services/api";
import Logo from '../common/Logo';

const { Header } = Layout;

const StyledHeader = styled(Header)`
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 1000;
  display: flex;
  align-items: center;
  padding: 0 24px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(5px);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03), 
              0 2px 4px rgba(0, 0, 0, 0.03), 
              0 4px 8px rgba(0, 0, 0, 0.03);
  height: 64px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.98);
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  flex: 1;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1.5rem;
  font-weight: bold;
  color: #1890ff;
  cursor: pointer;
  
  span {
    font-size: 1.2rem;
  }
  
  &:hover {
    opacity: 0.8;
  }
`;

const StyledBreadcrumb = styled(Breadcrumb)`
  .ant-breadcrumb-link {
    display: flex;
    align-items: center;
    gap: 6px;
    color: rgba(0, 0, 0, 0.45);
    transition: all 0.3s ease;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 4px;

    &:hover {
      color: #1890ff;
      background: rgba(24, 144, 255, 0.1);
    }

    .anticon {
      font-size: 16px;
    }
  }

  .ant-breadcrumb-separator {
    display: none;
  }

  ol {
    display: flex;
    gap: 8px;
  }
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  .user-info {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    padding: 4px;
    border-radius: 6px;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(0, 0, 0, 0.02);
    }

    .email {
      color: rgba(0, 0, 0, 0.65);
      font-size: 14px;
      max-width: 200px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .ant-btn {
    height: 36px;
    border-radius: 6px;
    font-weight: 500;
    padding: 0 20px;
    transition: all 0.2s ease;
    
    &.ant-btn-text {
      color: #666;
      
      &:hover {
        color: #1890ff;
        background: rgba(24, 144, 255, 0.1);
      }
    }
    
    &.ant-btn-primary {
      background: #1890ff;
      border-color: #1890ff;
      box-shadow: 0 2px 0 rgba(24, 144, 255, 0.1);
      
      &:hover {
        background: #40a9ff;
        border-color: #40a9ff;
        transform: translateY(-1px);
        box-shadow: 0 4px 8px rgba(24, 144, 255, 0.2);
      }
    }
  }
  
  .ant-avatar {
    background: #e6f4ff;
    border: 2px solid white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease;
    
    .anticon {
      color: #1890ff;
    }
    
    &:hover {
      transform: scale(1.05);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
  }
`;

const StyledMenuItem = styled.div`
  &.upgrade {
    color: #f5a623;
    font-weight: 500;
    
    .anticon {
      color: #f5a623;
    }
  }
`;

const StyledAvatar = styled(Avatar)`
  &.ant-avatar {
    background: #e6f4ff;
    color: #1890ff;
    cursor: pointer;
    
    .anticon {
      color: #1890ff;
    }
  }
`;

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && !user) {
      const fetchUser = async () => {
        try {
          const userData = await auth.getCurrentUser();
          dispatch(setUser(userData));
        } catch (error) {
          console.error('Failed to fetch user:', error);
          localStorage.removeItem('token');
        }
      };
      fetchUser();
    }
  }, [dispatch, user]);

  useEffect(() => {
    const fetchAvatar = async () => {
      if (user?.avatarUrl) {
        try {
          const filename = user.avatarUrl.split('/').pop();
          if (filename) {
            const url = await auth.getAvatar(filename);
            setAvatarUrl(url);
          }
        } catch (error) {
          console.error('获取头像失败:', error);
        }
      }
    };
    fetchAvatar();
  }, [user?.avatarUrl]);

  const handleLogout = () => {
    auth.logout();
    dispatch(clearUser());
    setAvatarUrl(null);
    navigate('/');
  };

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'projects',
      icon: <ProjectOutlined />,
      label: '我的项目',
      onClick: () => navigate('/app')
    },
    {
      type: 'divider'
    },
    {
      key: 'upgrade',
      icon: <CrownOutlined />,
      label: <StyledMenuItem className="upgrade">{user?.isPremium ? '续费会员' : '升级会员'}</StyledMenuItem>,
      onClick: () => navigate('/pricing')
    },
    {
      type: 'divider'
    },
    {
      key: 'profile',
      icon: <SettingOutlined />,
      label: '个人设置',
      onClick: () => navigate('/profile')
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: handleLogout
    }
  ];

  const handleLogoClick = () => {
    navigate('/');
  };

  return (
    <StyledHeader>
      <LeftSection>
        <LogoContainer onClick={handleLogoClick}>
          <Logo size={36} />
          <span>来创业</span>
        </LogoContainer>
        {user && (
          <StyledBreadcrumb
            items={[
              {
                title: (
                  <div className="ant-breadcrumb-link" onClick={() => navigate('/app')}>
                    <AppstoreOutlined />
                    <span>开始创业</span>
                  </div>
                )
              }
            ]}
          />
        )}
      </LeftSection>

      <UserSection>
        {user ? (
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <div className="user-info">
              <StyledAvatar 
                icon={<UserOutlined />}
                src={avatarUrl}
              />
              <span className="email">{user.email}</span>
            </div>
          </Dropdown>
        ) : (
          <>
            <Button type="text" onClick={() => navigate('/auth')}>登录</Button>
            <Button type="primary" onClick={() => navigate('/auth?tab=register')}>
              注册
            </Button>
          </>
        )}
      </UserSection>
    </StyledHeader>
  );
};

export default Navbar;